import type { Auth0VueClientOptions } from '@auth0/auth0-vue';
import { createAuth0 } from '@auth0/auth0-vue';
import { defineNuxtPlugin, useRuntimeConfig, navigateTo } from 'nuxt/app';
import { watch } from 'vue';
import { getPostLoginRedirect, setLoggedIn } from '~/composables/usePostLoginRedirect';
import useAuthStore from '~/stores/auth';

export default defineNuxtPlugin((nuxtApp) => {
   if (process.server) {
      return;
   }

   const config = useRuntimeConfig();

   const auth0Params: Auth0VueClientOptions = {
      domain: config.public.auth0Domain as string,
      clientId: config.public.auth0ClientId as string,
      authorizationParams: {
         audience: config.public.auth0Audience as string,
         redirect_uri: window.location.origin + '/lms', // Correct - auth routes are under /lms/
      },
   };

   const auth0 = createAuth0(auth0Params);

   nuxtApp.vueApp.use(auth0);
   nuxtApp.provide('auth0', auth0);

   const unwatchLoading = watch(
      () => auth0.isLoading.value,
      async (isLoading) => {
         if (isLoading) {
            return;
         }

         unwatchLoading();

         // When Auth0 restores an existing session on a normal load (not a login
         // callback — that path sets the token itself), the accessToken was never
         // populated. Without it, authenticated views mounted on `isAuthenticated`
         // fired requests with no token. Fetch it here so the token exists as soon
         // as the app knows the user is authenticated. A failure (e.g. expired
         // refresh token) leaves accessToken undefined, keeping the user on public
         // views instead of sending an invalid token.
         if (!auth0.isAuthenticated.value) {
            return;
         }

         const authStore = useAuthStore();
         if (authStore.accessToken) {
            return;
         }

         try {
            authStore.accessToken = await auth0.getAccessTokenSilently();
         } catch (error) {
            console.error('Failed to restore Auth0 access token', error);
         }
      },
      { immediate: true }
   );

   // Watch for Auth0 callback completion and handle redirect
   // This prevents NAVIGATION_DUPLICATED errors by waiting for Auth0 to finish
   // before navigating to the post-login redirect URL
   const hasCodeInUrl = window.location.search.includes('code=');
   if (hasCodeInUrl) {
      let callbackExecuted = false;

      const unwatch = watch(
         () => auth0.isLoading.value,
         async (isLoading) => {
            if (callbackExecuted) return;

            // When Auth0 finishes loading (callback processed), navigate to target
            if (!isLoading && auth0.isAuthenticated.value) {
               callbackExecuted = true;

               // Fetch token and profile before deciding redirect target.
               // The API returns post_signup_redirect in site_settings when a
               // first-time user should be sent to a specific page (e.g. memberships).
               // The profile await also ensures Auth0's internal navigation settles
               // before we call navigateTo.
               setLoggedIn();

               // Signal the middleware that a callback redirect is pending.
               // During the profile await, Auth0 SDK navigates to / internally,
               // which would flash the public homepage. The middleware checks this
               // flag and redirects immediately instead.
               sessionStorage.setItem('auth0_callback_pending', 'true');
               let targetUrl = getPostLoginRedirect();

               try {
                  const authStore = useAuthStore();

                  if (!authStore.accessToken) {
                     authStore.accessToken = await auth0.getAccessTokenSilently();
                  }

                  await authStore.setCurrentUserProfile();

                  // Persist portal selection on first login (covers /login/[portal] and ?portal= flows)
                  const selectedPortal = localStorage.getItem('selectedPortal');
                  if (selectedPortal) {
                     authStore.persistPortalSelection(selectedPortal);
                  }

                  const postSignupRedirect = authStore.user.site_settings.post_signup_redirect;
                  if (postSignupRedirect) {
                     targetUrl = postSignupRedirect;
                  }
               } catch {
                  // Profile fetch failed, using fallback redirect
               }

               // unwatch() is called after awaits to avoid temporal dead zone
               // when immediate: true fires the callback before watch() returns
               unwatch();

               // Use setTimeout to ensure Auth0 has fully completed its internal state updates
               setTimeout(() => {
                  navigateTo(targetUrl, { replace: true });
               }, 0);
            }
         },
         { immediate: true }
      );
   }
});
